import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  getUserInformation,
  updateUserData,
  saveUserAvatar,
  resetAvatar,
} from '../../../../redux/users/actions';
import UserProfileView from '../UserProfileView/UserProfileView';
import ValidationUtil from '../../../../util/ValidationUtil';
import { PolyglotComponentProps, withPolyglot } from '../../../../i18n';
import {
  HandlingErrorWrappedProps,
  withHandlingErrors,
} from '../../../../handlingErrors';
import { RootState } from '../../../../redux/store.model';
import { UserAPIResponse } from '../../../../redux/users/api/user.model';
import { Container, CircularProgress, Box, Typography } from '@mui/material';
import { IconUpload } from '../../../../redux/groups/groups.model';
import { getApplicationsFeatureToggle } from '../../../../featureToggle';
import { saveSettings } from '../../../../redux/settings/thunks';
import {
  ShowMessageProps,
  withShowMessage,
} from '../../../HOC/withShowMessage';
import packageInfo from '../../../../../package.json';

const Kbyte = 1024;
const maxSizeImageKbyte = 1024; // maximal size in kilobytes 1MB
const maxHeightImage = 500; // 500px
const maxWidthImage = 500; // 500px

interface Props
  extends PolyglotComponentProps,
    ConnectedComponentProps,
    HandlingErrorWrappedProps,
    ShowMessageProps {}

interface State {
  user: any | Partial<UserAPIResponse>;
  iconUpload: Partial<IconUpload>;
  showImageLoad?: boolean;
  loading?: boolean;
}

export class UserProfile extends Component<Props, State> {
  validationUtil: ValidationUtil;
  constructor(props: Props) {
    super(props);
    this.getUserInformation = this.getUserInformation.bind(this);
    this.onChangeImage = this.onChangeImage.bind(this);
    this.imageValidation = this.imageValidation.bind(this);
    this.save = this.save.bind(this);
    this.resetAvatar = this.resetAvatar.bind(this);
    this.validationUtil = new ValidationUtil(props.polyglot);
    this.state = {
      loading: true,
      user: {},
      iconUpload: {
        fileName: '',
        imagePreviewUrl: '',
      },
    };
  }

  async componentDidMount() {
    await this.getUserInformation();
  }

  onChangeImage(file: React.ChangeEvent<HTMLInputElement>) {
    const iconFile = file.target.files && file.target.files[0];
    const { iconUpload } = this.state;
    if (!iconFile) {
      return;
    }
    this.imageValidation(iconFile)
      .then(() => {
        iconUpload.fileName = iconFile.name;
        iconUpload.file = iconFile;
        this.setState({
          iconUpload,
          showImageLoad: true,
        });
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          iconUpload.imagePreviewUrl = fileReader.result as string;
          this.setState({
            iconUpload,
            showImageLoad: false,
          });
        };
        fileReader.readAsDataURL(iconFile);
      })
      .catch(this.props.handlingErrorsApi);
  }

  async getUserInformation() {
    try {
      await this.props.getUserInformation();
      const { user } = this.props;
      this.setState({
        user,
        loading: false,
      });
    } catch (error) {
      const { handlingErrorsApi } = this.props;
      handlingErrorsApi(error);
    }
  }

  showApplications = () => {
    return this.props.listApplications.length > 1;
  };

  async saveSettings(settings: {
    applicationDefault?: string;
    temperatureUnitPreferred?: string;
  }) {
    await this.props.saveSettings(settings);
  }

  imageValidation(image: File) {
    const { polyglot } = this.props;
    const promise = new Promise((resolve, reject) => {
      let message = {};
      if (
        image.type.indexOf('image') === -1 ||
        !ValidationUtil.validateImageFormat(image)
      ) {
        message = {
          text: polyglot.t('error.only_format_images'),
          type: 'error',
        };
        reject(message);
      }
      if (image.size > maxSizeImageKbyte * Kbyte) {
        message = {
          text: `${polyglot.t(
            'error.images_size_restriction'
          )} ${maxSizeImageKbyte} ${polyglot.t('general.KB')}`,
          type: 'error',
        };
        reject(message);
      }

      const URL = window.URL || window.webkitURL;
      const img = new Image();
      img.onload = () => {
        if (img.width > maxWidthImage) {
          message = {
            text: `${polyglot.t('error.images_width_restriction')} ${polyglot.t(
              'general.max_width'
            )}: ${maxWidthImage} ${polyglot.t('general.px')}`,
            type: 'error',
          };
          reject(message);
        } else if (img.height > maxHeightImage) {
          message = {
            text: `${polyglot.t(
              'error.images_height_restriction'
            )} ${polyglot.t(
              'general.max_height'
            )}: ${maxHeightImage} ${polyglot.t('general.px')}`,
            type: 'error',
          };
          reject(message);
        }
        resolve(true);
      };
      img.src = URL.createObjectURL(image);
    });
    return promise;
  }

  async resetAvatar() {
    const { showMessage, polyglot, handlingErrorsApi } = this.props;
    try {
      await this.props.resetAvatar();
      const { user } = this.state;
      this.setState({
        iconUpload: {
          fileName: '',
          imagePreviewUrl: '',
        },
        user: { ...user, avatarLocation: null },
      });
      showMessage(polyglot.t('user.reset_avatar_successful_message'));
    } catch (error) {
      handlingErrorsApi(error);
    }
  }

  async save(
    values: Partial<
      UserAPIResponse & {
        applicationDefault?: string;
        temperatureUnitPreferred?: string;
      }
    >
  ) {
    const { applicationDefault, temperatureUnitPreferred, ...user } = values;
    try {
      const { iconUpload } = this.state;
      await this.props.updateUserData(user as Partial<UserAPIResponse>);
      if (iconUpload.fileName !== '') {
        await this.props.saveUserAvatar(iconUpload as IconUpload);
      }
      await this.saveSettings({ applicationDefault, temperatureUnitPreferred });
      this.setState({ user });
      const { polyglot, showMessage } = this.props;
      showMessage(polyglot.t('user.save_group_successful_message'));
    } catch (error) {
      const { handlingErrorsApi } = this.props;
      handlingErrorsApi(error);
    }
  }

  render() {
    const { user, iconUpload, showImageLoad, loading } = this.state;

    return (
      <>
        <Container maxWidth="md">
          {loading ? (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <UserProfileView
              user={user}
              onChangeImage={this.onChangeImage}
              iconUpload={iconUpload}
              resetAvatar={this.resetAvatar}
              showImageLoad={showImageLoad}
              save={this.save}
              imageValidation={this.imageValidation}
              showApplications={this.showApplications()}
              listApplications={this.props.listApplications}
            />
          )}
        </Container>
        <Container maxWidth="md" sx={{ backgroundColor: 'inherit' }}>
          <Typography
            variant="caption"
            color="text.disabled"
          >{`V ${packageInfo.version}`}</Typography>
        </Container>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.users.item,
  listApplications: getApplicationsFeatureToggle(state),
});
const connector = connect(mapStateToProps, {
  getUserInformation,
  updateUserData,
  saveUserAvatar,
  saveSettings: saveSettings,
  resetAvatar,
});
type ConnectedComponentProps = ConnectedProps<typeof connector>;

export default connector(
  withHandlingErrors(withShowMessage(withPolyglot(UserProfile)))
);
